import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {PermissionAccess} from 'components/common/PermissionAccess';
import {CustomHsnCodes} from 'components/pages/DocumentsCreate/InvoiceNew/FormsData/selectOptionsComponents/CustomHsnCodes';
import {useHsnFetching} from 'components/pages/DocumentsCreate/InvoiceNew/hooks/useHsnFetching';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Helper} from 'components/ui-kit/Helper';
import {HtmlText} from 'components/ui-kit/HTMLText';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {Spinner} from 'components/ui-kit/Spinner';
import {Switch} from 'components/ui-kit/Switcher';
import {apiClient} from 'core/apiClient';
import {COUNTRY} from 'core/constants/env';
import {ProductGroups} from 'core/constants/productGroups';
import {$mainRole} from 'models/auth';
import {
    deleteGtinApiV2Fx,
    editGtinApiV2Fx,
    formData$,
    isFormModalOpen$,
    isFormModalOpenChanged,
    uploadDataApiV2Fx,
} from 'models/registries/gtinsGrid/upload';
import {UserPermission, UserRoles} from 'types';
import {CUSTOMER_PACKAGE_TYPES, getAvailableProductStatuses, ProductStatuses} from 'types/gtins';
import {OrganizationRawRow} from 'types/profile';
import {usePermission} from 'utils/custom-hooks';
import {mapEnumToI18nOptions} from 'utils/select';

import {
    ButtonsContainer,
    ButtonsContainerWrapper,
    CustomContentContainer,
    Field,
    FormFields,
    ReportBugContainer,
    ReportBugDescription,
    ReportBugHeadline,
    SpinnerLayer,
} from './styled';

type AttributeConfig = {
    id: number;
    deploymentCustomer: number | null;
    fieldSection: string;
    fieldName: string;
    isVisible: boolean;
    isRequired: boolean;
    isAutoFill: boolean;
};

type FormData = Record<string, any>;

type StatClassifier = {
    id: number;
    code: string;
    valueRu: string;
    valueTj: string;
};

type CNFEA = {
    positionName: string;
    code: string;
};

type Country = {
    country: string;
    countryFr: string;
    code: string;
};

type PackagingType = {
    name: string;
    nameEn: string;
    nameFr: string;
};

export const GtinsUploadForm = () => {
    const {t, i18n} = useTranslation();
    const [loading, setLoading] = useState(false);
    const mainRole = useStore($mainRole);

    const isOpen = useStore(isFormModalOpen$);
    const editPending = useStore(editGtinApiV2Fx.pending);
    const editData = useStore(formData$)?.[0];

    const hasEditPermission = usePermission([UserPermission.GTIN_CREATE]);

    const FORM_LOCAL_PATH = 'pages.registries.uploadSingleGtin.form.';
    const translateForm = useCallback((key: string) => t(FORM_LOCAL_PATH + 'fields.' + key), [
        i18n.language,
    ]);
    const translateValidates = useCallback(
        (key: string) => t(FORM_LOCAL_PATH + 'validates.' + key),
        [i18n.language],
    );

    const [attributesConfig, setAttributesConfig] = useState<AttributeConfig[]>([]);
    const [registries, setRegistries] = useState<
        Partial<{
            statClassifier: StatClassifier[];
            cnfea: CNFEA[];
            countries: Country[];
            packagingTypes: PackagingType[];
        }>
    >({});
    const getDictionaries = async () => {
        if (Object.keys(registries).length >= 3) return;
        const [statClassifier, cnfea, countries, packagingTypes] = await Promise.all([
            apiClient.get<StatClassifier[]>('service/dict/stat-classifier').then(({data}) => data),
            apiClient.get<CNFEA[]>('service/dict/cnfea').then(({data}) => data),
            apiClient.get<Country[]>('service/dict/countries').then(({data}) => data),
            apiClient
                .get<PackagingType[]>('service/dict/packaging-format-type')
                .then(({data}) => data),
        ]);
        setRegistries({statClassifier, cnfea, countries, packagingTypes});
    };
    const getConfig = async () => {
        const products = Object.keys(getValues()).length ? getValues() : editData ?? {};
        const config = (
            await apiClient.post<AttributeConfig[]>(
                'service/attribute-config/new',
                {products: [products]},
                {params: {fieldSection: 'PRODUCT_CARD', customer: COUNTRY}},
            )
        ).data;

        const isActiveElementIndex = config.findIndex(
            (element) => element.fieldName === 'is_active',
        );
        const [elementToMove] = config.splice(isActiveElementIndex, 1);
        config.unshift(elementToMove);

        setAttributesConfig(config);
    };

    const init = async () => {
        try {
            setLoading(true);
            await Promise.all([getConfig(), getDictionaries()]);
        } catch (error) {
            isFormModalOpenChanged(false);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (editPending) return;
        if (!isOpen) {
            reset(defaultValues);
            return;
        }
        if (isOpen && editData) {
            init().then(() => reset(editData));
            return;
        }

        init().then(() => {
            const initialValues = {...defaultValues};
            if (mainRole !== UserRoles.ROLE_MPL_OPERATOR_ADMIN) {
                initialValues.status = ProductStatuses.PENDING;
            }
            reset(initialValues);
        });
    }, [isOpen, editData, mainRole]);

    const validationSchema = useMemo(() => {
        const shape: Record<string, any> = {};
        attributesConfig.forEach((field) => {
            if (field.fieldName === 'is_active') {
                shape[field.fieldName] = yup.boolean();
                return;
            }
            shape[field.fieldName] = field.isRequired
                ? yup.string().required(translateValidates('required'))
                : yup
                      .string()
                      .nullable()
                      .transform((v) => v || null);
        });

        return yup.object().shape(shape);
    }, [attributesConfig, translateValidates]);

    const defaultValues = useMemo(() => {
        const values: Record<string, string | boolean> = {};
        attributesConfig.forEach((field) => {
            if (field.fieldName === 'is_active') {
                values[field.fieldName] = true;
                return;
            }

            // if (field.fieldName === 'status' && mainRole !== UserRoles.ROLE_MPL_OPERATOR_ADMIN) {
            //     values[field.fieldName] = ProductStatuses.PENDING;
            //     return;
            // }
            values[field.fieldName] = '';
        });
        return values;
    }, [attributesConfig, mainRole]);

    const {
        control,
        formState: {errors},
        reset,
        watch,
        setValue,
        setError,
        trigger,
        handleSubmit,
        getValues,
    } = useForm<FormData>({
        resolver: yupResolver(validationSchema),
        defaultValues,
        mode: 'all',
    });

    const taxIdNumber = watch('tax_id_number');
    const fetchLegalName = async () => {
        if (!taxIdNumber) return;
        try {
            const response = await apiClient.get<OrganizationRawRow>(
                `service/dict/legal-identity-address`,
                {
                    params: {taxIdNumber},
                },
            );

            const {
                legalName,
                address: {address},
            } = response.data;

            if (legalName) setValue('legal_name', legalName);
            if (address) setValue('legal_address', address);
            trigger(['legal_name', 'legal_address']);
        } catch (error) {
            console.error('Fetch legal name error:', error);
        }
    };

    const codeVed = watch('code_ved');
    useEffect(() => {
        if (!codeVed) return;
        const category = registries?.cnfea?.find((item) => item.code === codeVed)?.positionName;
        if (category) setValue('category_ved', category);
    }, [codeVed]);

    const country_manufacture = watch('country_manufacture');
    const productGroupCode = watch('product_group_code');
    const packageType = watch('package_type');
    useEffect(() => {
        if (!isOpen || loading || editPending) return;
        getConfig().then(() => editData && trigger());
    }, [packageType, productGroupCode, country_manufacture, setValue]);

    const onSubmit = async (form: FormData) => {
        const fieldsToTrim = ['gtin'];
        const trimmedForm = fieldsToTrim.reduce((acc, field) => {
            acc[field] = acc[field] ? acc[field].toString().trim() : '';
            return acc;
        }, form);

        try {
            await uploadDataApiV2Fx([trimmedForm]);
        } catch (error) {
            const responseData = (error as any).fullErrorsList;
            if (!Array.isArray(responseData)) return;

            responseData.forEach(({field, error}) =>
                setError(field, {
                    type: 'manual',
                    message: error,
                }),
            );
        }
    };

    const loadOptions = useHsnFetching();

    const controlRender = (
        controlEvents: ControllerRenderProps<FormData>,
        attribute: AttributeConfig,
    ) => {
        const baseProps = {
            // placeholder: translateForm(attribute.fieldName),
            hasError: !!errors?.[attribute.fieldName],
            placeholder:
                attribute.fieldName === 'category_ved'
                    ? translateValidates('auto')
                    : translateForm(attribute.fieldName),
            disabled:
                !hasEditPermission ||
                attribute.fieldName === 'category_ved' ||
                attribute.fieldName === 'hsn_description' ||
                (editData &&
                    ['packaging_type', 'gtin', 'package_type'].includes(attribute.fieldName)) ||
                (editData && mainRole !== UserRoles.ROLE_MPL_OPERATOR_ADMIN),
        };
        const onBlurWithValidates = () => {
            controlEvents.onBlur();
            if (attribute.fieldName === 'tax_id_number') fetchLegalName();
        };

        if (attribute.fieldName === 'is_active') {
            const {ref, ...rest} = controlEvents;
            return (
                <div style={{paddingBottom: '2px'}}>
                    <Switch
                        {...rest}
                        font={
                            attribute.fieldName === 'is_active'
                                ? {size: '14px', color: 'textSecondary'}
                                : undefined
                        }
                        width="100%"
                        label={t('pages.registries.uploadSingleGtin.form.isActiveLabel')}
                        onChange={(value) => controlEvents.onChange(value)}
                    />
                </div>
            );
        }

        const dropdownFields = [
            'code_stat_classifier',
            'package_type',
            'product_group_code',
            'code_ved',
            'packaging_type',
            'country_manufacture',
        ];

        if (attribute.fieldName === 'status' && mainRole === UserRoles.ROLE_MPL_OPERATOR_ADMIN) {
            dropdownFields.push('status');
        }

        if (dropdownFields.includes(attribute.fieldName)) {
            let options: SelectOption<any>[] = [];
            let formatOptionLabel: ((option: any, context: {context: any}) => any) | undefined;

            if (attribute.fieldName === 'code_stat_classifier') {
                options = (registries.statClassifier || []).map((item) => ({
                    value: item.code,
                    label: item.valueRu,
                }));
            }

            if (attribute.fieldName === 'package_type') {
                options = CUSTOMER_PACKAGE_TYPES.map((type) => ({
                    value: type,
                    label: t(`dictionaries.products.packageType.${type}`),
                }));
            }

            if (attribute.fieldName === 'product_group_code') {
                options = mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups');
            }

            if (attribute.fieldName === 'code_ved') {
                options = (registries.cnfea || []).map((item) => ({
                    value: item.code,
                    label: `${item.code} ${item.positionName}`,
                }));
                formatOptionLabel = (option: any, {context}: {context: any}) =>
                    context === 'value' ? option.value : option.label;
            }

            if (attribute.fieldName === 'packaging_type') {
                options = (registries.packagingTypes || []).map((item) => ({
                    value: item.name,
                    label: i18n.language === 'fr' ? item.nameFr : item.nameEn,
                }));
            }

            if (attribute.fieldName === 'country_manufacture') {
                options = (registries.countries || []).map((item) => ({
                    label: (i18n.language === 'en' ? item.country : item.countryFr) ?? item.country,
                    value: item.code,
                }));
            }

            if (attribute.fieldName === 'status') {
                const availableStatuses = getAvailableProductStatuses(mainRole);
                options = mapEnumToI18nOptions(availableStatuses, 'dictionaries.productStatuses');
            }

            return (
                <div style={{paddingBottom: '2px'}}>
                    <Select
                        value={options.find((o) => o.value === controlEvents.value) ?? null}
                        {...(formatOptionLabel && {formatOptionLabel})}
                        options={options}
                        onBlur={controlEvents.onBlur}
                        onChange={(option: SelectOption<number>) =>
                            controlEvents.onChange(option.value)
                        }
                        {...baseProps}
                    />
                </div>
            );
        }

        const dropdownAsyncFields = ['hsn_code'];
        if (dropdownAsyncFields.includes(attribute.fieldName)) {
            if (attribute.fieldName === 'hsn_code') {
                return (
                    <div style={{paddingBottom: '2px'}}>
                        <AsyncSelect
                            {...baseProps}
                            value={
                                controlEvents.value
                                    ? {value: controlEvents.value, label: controlEvents.value}
                                    : null
                            }
                            isSearchable={true}
                            defaultOptions={[]}
                            menuPlacement={'bottom'}
                            menuPosition={'fixed'}
                            optionComponent={CustomHsnCodes}
                            loadOptions={loadOptions}
                            placeholder={'HSN code'}
                            onChange={(option: SelectOption<string>) => {
                                controlEvents.onChange(option.value);
                                setValue('hsn_description', option.label);
                            }}
                        />
                    </div>
                );
            }
        }

        const statusInputField =
            attribute.fieldName === 'status' && mainRole !== UserRoles.ROLE_MPL_OPERATOR_ADMIN;

        const props = {...baseProps, ...controlEvents};

        if (statusInputField) {
            props.disabled = true;
            const statusValue =
                controlEvents.value || (editData ? editData.status : ProductStatuses.PENDING);
            props.value = statusValue || '';
        }

        return <Input {...props} onBlur={onBlurWithValidates} />;
    };

    const GPCHelper = () => (
        <Helper
            style={{marginLeft: 5}}
            content={
                <HtmlText
                    __html={t('pages.registries.uploadSingleGtin.helper.gps_code', {
                        link: 'https://gpc-browser.gs1.org/',
                    })}
                />
            }
        />
    );

    const ReportBug = () => (
        <ReportBugContainer>
            <ReportBugHeadline>{t('pages.registries.bugReport.title')}</ReportBugHeadline>
            <ReportBugDescription>
                <HtmlText
                    __html={t('pages.registries.bugReport.description', {
                        link: 'mailto:info@rcatfe.com',
                    })}
                />
            </ReportBugDescription>
        </ReportBugContainer>
    );

    const ModalFooter = () => (
        <ButtonsContainerWrapper>
            {editData && editData.status === ProductStatuses.PUBLISHED && <ReportBug />}
            <ButtonsContainer>
                {editData && mainRole === UserRoles.ROLE_MPL_OPERATOR_ADMIN ? (
                    <></>
                ) : (
                    <Button
                        variant={ButtonVariant.Outline}
                        onClick={() => isFormModalOpenChanged(false)}
                    >
                        {t('components.modal.Close')}
                    </Button>
                )}
                {hasEditPermission ? (
                    <>
                        {editData && (
                            <PermissionAccess
                                permissions={[]}
                                roleAccess={[UserRoles.ROLE_MPL_OPERATOR_ADMIN]}
                            >
                                <Button
                                    variant={ButtonVariant.Cancel}
                                    type="button"
                                    onClick={() => {
                                        deleteGtinApiV2Fx(editData?.gtin);
                                    }}
                                >
                                    {t('components.form.delete')}
                                </Button>
                            </PermissionAccess>
                        )}
                        <Button
                            variant={editData ? ButtonVariant.Secondary : ButtonVariant.Normal}
                            type="submit"
                        >
                            {t(
                                editData
                                    ? 'components.form.saveChanges'
                                    : 'components.form.sendForReview',
                            )}
                        </Button>
                    </>
                ) : (
                    <Button onClick={() => isFormModalOpenChanged(false)}>
                        {t('components.modal.Ok')}
                    </Button>
                )}
            </ButtonsContainer>
        </ButtonsContainerWrapper>
    );

    const ModalSpinner = () => (
        <SpinnerLayer style={{minHeight: 300}}>
            <Spinner />
        </SpinnerLayer>
    );

    return (
        <Modal
            show={isOpen}
            onClose={() => isFormModalOpenChanged(false)}
            title={t('pages.registries.uploadFormGtinTitle')}
        >
            <CustomContentContainer>
                {loading ? (
                    <ModalSpinner />
                ) : (
                    <form onSubmit={handleSubmit(onSubmit, console.error)}>
                        <FormFields>
                            {attributesConfig
                                .filter((field) => field.isVisible)
                                .map((attribute) => (
                                    <Field
                                        font={
                                            attribute.fieldName === 'is_active'
                                                ? {size: '16px', color: 'darkGrey'}
                                                : undefined
                                        }
                                        key={attribute.id}
                                        text={errors?.[attribute.fieldName]?.message}
                                        isError={!!errors?.[attribute.fieldName]}
                                    >
                                        <span style={{display: 'flex', alignItems: 'flex-end'}}>
                                            {translateForm(attribute.fieldName)}
                                            {attribute.fieldName === 'gps_code' && GPCHelper()}
                                        </span>
                                        <Controller
                                            name={attribute.fieldName}
                                            control={control}
                                            defaultValue={defaultValues[attribute.fieldName] ?? ''}
                                            render={(controlEvents) =>
                                                controlRender(controlEvents, attribute)
                                            }
                                        />
                                    </Field>
                                ))}
                        </FormFields>
                        <ModalFooter />
                    </form>
                )}
            </CustomContentContainer>
        </Modal>
    );
};
